import { useMemo, type FC } from 'react';
import { Box, useDisclosure } from '@chakra-ui/react';
import SearchBar from './components/SearchBar';
import { SearchBreadcrumbs } from './components/SearchBreadcrumbs';
import { SearchContext } from './types';
import { type OperationViewModel } from '../Map/types';
import { type Page, type TargetVessel } from '../../types';
import { type Timestamp } from '../../services/Timestamp';

interface Props {
  operation: OperationViewModel;
  operationKey: string;
  isWorldView: boolean;
  page: Page;
  timestamp: Timestamp;
  targetVessel: TargetVessel;
  timezone: string;
  isVesselDataLoading: boolean;
  onOperationSelected: (operationKey: string) => void;
  onVesselPositionReceived: (mmsi: number, operation: string, vesselName: string, timestamp?: Timestamp) => void;
  onVesselSelected: (mmsi: string, vesselName: string) => void;
  onJobOrTripSelected: (operation: string, mmsi: string, timestamp: Timestamp, vesselName: string) => void;
}

export const GlobalSearch: FC<Props> = ({
  operation,
  operationKey,
  isWorldView,
  page,
  timestamp,
  targetVessel,
  timezone,
  isVesselDataLoading,
  onOperationSelected,
  onVesselPositionReceived,
  onVesselSelected,
  onJobOrTripSelected,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const operationName = operation?.name ?? operationKey;

  const searchContext = useMemo(() => {
    if (!!operationName && !!targetVessel?.mmsi && (!!targetVessel?.name || isVesselDataLoading)) {
      return SearchContext.Vessel;
    }

    if (operationName) {
      return SearchContext.Operation;
    }

    return SearchContext.Global;
  }, [operationName, targetVessel]);

  return (
    <Box className="global-search-container">
      <SearchBreadcrumbs
        operation={operationName}
        isWorldView={isWorldView}
        targetVessel={targetVessel}
        onBreadcrumbClick={onOpen}
        onOperationSelect={onOperationSelected}
        onVesselSelected={onVesselSelected}
        context={searchContext}
      />
      {isOpen && <div className="overlay" />}
      <SearchBar
        operation={operationName}
        page={page}
        timestamp={timestamp}
        targetVessel={targetVessel}
        timezone={timezone}
        isSearchOpen={isOpen}
        context={searchContext}
        onOpen={onOpen}
        onClose={onClose}
        onVesselPositionReceived={onVesselPositionReceived}
        onOperationSelected={onOperationSelected}
        onJobOrTripSelected={onJobOrTripSelected}
      />
    </Box>
  );
};
